import React from 'react';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  TrendingUpIcon,
  UsersIcon,
  MessageSquareIcon,
  DollarSignIcon,
  TargetIcon,
} from 'lucide-react';

interface Metrics {
  total_opportunities?: number;
  total_comments_generated?: number;
  total_comments_posted?: number;
  total_conversions?: number;
  total_revenue?: number;
  conversion_rate?: number;
  average_roi?: number;
}

interface Trends {
  current?: Metrics;
  previous?: Metrics;
  changes?: Record<string, number>;
}

interface PerformanceMetricsProps {
  data?: Metrics;
  trends?: Trends;
  timeframe?: string;
}

export default function PerformanceMetrics({ data, trends, timeframe = '7d' }: PerformanceMetricsProps) {
  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-500">Loading performance metrics...</p>
      </div>
    );
  }

  const metrics = [
    {
      name: 'Opportunities Discovered',
      value: data.total_opportunities || 0,
      change: trends?.changes?.total_opportunities || 0,
      icon: TargetIcon,
      color: 'blue',
      prefix: '',
      suffix: '',
    },
    {
      name: 'Comments Generated',
      value: data.total_comments_generated || 0,
      change: trends?.changes?.total_comments_generated || 0,
      icon: MessageSquareIcon,
      color: 'purple',
      prefix: '',
      suffix: '',
    },
    {
      name: 'Comments Posted',
      value: data.total_comments_posted || 0,
      change: trends?.changes?.total_comments_posted || 0,
      icon: TrendingUpIcon,
      color: 'green',
      prefix: '',
      suffix: '',
    },
    {
      name: 'Conversions',
      value: data.total_conversions || 0,
      change: trends?.changes?.total_conversions || 0,
      icon: UsersIcon,
      color: 'orange',
      prefix: '',
      suffix: '',
    },
    {
      name: 'Total Revenue',
      value: data.total_revenue || 0,
      change: trends?.changes?.total_revenue || 0,
      icon: DollarSignIcon,
      color: 'emerald',
      prefix: '$',
      suffix: '',
    },
    {
      name: 'Conversion Rate',
      value: data.conversion_rate || 0,
      change: trends?.changes?.conversion_rate || 0,
      icon: TargetIcon,
      color: 'pink',
      prefix: '',
      suffix: '%',
    },
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; icon: string }> = {
      blue: {
        bg: 'bg-blue-50',
        text: 'text-blue-600',
        icon: 'text-blue-600',
      },
      purple: {
        bg: 'bg-purple-50',
        text: 'text-purple-600',
        icon: 'text-purple-600',
      },
      green: {
        bg: 'bg-green-50',
        text: 'text-green-600',
        icon: 'text-green-600',
      },
      orange: {
        bg: 'bg-orange-50',
        text: 'text-orange-600',
        icon: 'text-orange-600',
      },
      emerald: {
        bg: 'bg-emerald-50',
        text: 'text-emerald-600',
        icon: 'text-emerald-600',
      },
      pink: {
        bg: 'bg-pink-50',
        text: 'text-pink-600',
        icon: 'text-pink-600',
      },
    };

    return colors[color] || colors.blue;
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toFixed(0);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {metrics.map((metric) => {
        const Icon = metric.icon;
        const colors = getColorClasses(metric.color);
        const isPositive = metric.change >= 0;
        const ChangeIcon = isPositive ? ArrowUpIcon : ArrowDownIcon;

        return (
          <div
            key={metric.name}
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-lg ${colors.bg}`}>
                <Icon className={`w-6 h-6 ${colors.icon}`} />
              </div>
              {metric.change !== 0 && (
                <div
                  className={`flex items-center gap-1 text-sm font-medium ${
                    isPositive ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  <ChangeIcon className="w-4 h-4" />
                  <span>{Math.abs(metric.change).toFixed(1)}%</span>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-600">{metric.name}</p>
              <p className={`text-3xl font-bold ${colors.text}`}>
                {metric.prefix}
                {formatNumber(metric.value)}
                {metric.suffix}
              </p>
            </div>

            <p className="text-xs text-gray-500 mt-2">
              {timeframe === '24h'
                ? 'Last 24 hours'
                : timeframe === '7d'
                ? 'Last 7 days'
                : timeframe === '30d'
                ? 'Last 30 days'
                : 'Last 90 days'}
            </p>
          </div>
        );
      })}
    </div>
  );
}
